import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';


const WhatsAppSessionManager = () => {
  const [sessions, setSessions] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(`${BACKEND_URL}/api/whatsapp/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('🔐 Please log in again');
      }
    }
  };

  const linkWhatsApp = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = secureStorage.getItem('nexus_access_token');

      // Create session
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/whatsapp/sessions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sessionId = createResponse.data.session_id;
      setCurrentSessionId(sessionId);

      // Poll for QR code
      pollForQR(sessionId);

    } catch (err) {
      console.error('Error linking WhatsApp:', err);
      const errorMsg = err.response?.data?.detail || '😅 Could not create session. Please try again.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const pollForQR = (sessionId) => {
    let attempts = 0;
    const maxAttempts = 15;

    const interval = setInterval(async () => {
      attempts++;

      try {
        const token = secureStorage.getItem('nexus_access_token');
        const response = await axios.get(
          `${BACKEND_URL}/api/whatsapp/sessions/${sessionId}/qr`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.qr_code) {
          setQrCode(response.data.qr_code);
          setLoading(false);
          clearInterval(interval);

          // Start polling for authentication
          pollForAuth(sessionId);
        }
      } catch (err) {
        // QR not ready yet, keep polling
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setLoading(false);
          setError('⏱️ Timeout waiting for QR code. Please try again.');
        }
      }
    }, 2000);
  };

  const pollForAuth = (sessionId) => {
    let attempts = 0;
    const maxAttempts = 60; // 3 minutes

    const interval = setInterval(async () => {
      attempts++;

      try {
        const token = secureStorage.getItem('nexus_access_token');
        const response = await axios.get(
          `${BACKEND_URL}/api/whatsapp/sessions/${sessionId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.status === 'authenticated') {
          clearInterval(interval);
          setQrCode(null);
          setCurrentSessionId(null);
          loadSessions();
          
          // Success notification
          alert('✅ WhatsApp linked successfully!');
        }
      } catch (err) {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setQrCode(null);
          setError('⏱️ Authentication timeout. Please try again.');
        }
      }
    }, 3000);
  };

  const disconnect = async (sessionId) => {
    if (!window.confirm('Disconnect this WhatsApp account?')) return;

    try {
      const token = secureStorage.getItem('nexus_access_token');
      await axios.delete(`${BACKEND_URL}/api/whatsapp/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      loadSessions();
      alert('✅ Disconnected successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '😅 Could not disconnect. Please try again.';
      alert(errorMsg);
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
      <h3 className="text-lg font-bold mb-2">📱 My WhatsApp Accounts</h3>
      <p className="text-xs text-gray-600 mb-4">
        Link your personal WhatsApp to send bulk messages
      </p>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Link Button */}
      <button
        onClick={linkWhatsApp}
        disabled={loading || qrCode}
        className="w-full mb-4 px-4 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all"
        style={{ minHeight: '44px' }}
      >
        {loading ? '⏳ Setting up...' : '+ Link My WhatsApp'}
      </button>

      {/* QR Code Modal */}
      {qrCode && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3"
          style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          <div 
            className="bg-white rounded-2xl p-4 max-w-md w-full shadow-2xl overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 24px)' }}
          >
            <h4 className="text-base font-bold mb-2 text-center">Scan with WhatsApp</h4>
            <p className="text-xs text-gray-600 mb-3 text-center leading-relaxed">
              1. Open WhatsApp on your phone<br/>
              2. Go to Settings → Linked Devices<br/>
              3. Tap "Link a Device"<br/>
              4. Scan this QR code
            </p>
            <div className="bg-white p-2 rounded-xl border-2 border-gray-200 mb-3 mx-auto" style={{ maxWidth: '280px' }}>
              <img src={qrCode} alt="QR Code" className="w-full" />
            </div>
            <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg border border-blue-200 mb-3">
              <span className="text-base">⏳</span>
              <span className="text-xs text-blue-800">
                Waiting for scan... This will close automatically once connected.
              </span>
            </div>
            <button
              onClick={() => {
                setQrCode(null);
                setCurrentSessionId(null);
              }}
              className="w-full px-4 py-2.5 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300"
              style={{ minHeight: '44px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Linked Accounts */}
      <div className="space-y-2 mb-4">
        {sessions.map((session) => {
          const phoneFormatted = session.phone_number 
            ? `+${session.phone_number.slice(0, 4)}••••••${session.phone_number.slice(-2)}`
            : 'Connecting...';
          
          const isActive = session.live_status === 'authenticated';
          
          return (
            <div
              key={session.session_id}
              className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{isActive ? '✅' : '⚠️'}</span>
                  <span className="font-bold text-sm">{phoneFormatted}</span>
                </div>
                <div className="text-xs text-gray-600">
                  {session.message || 'Status unknown'}
                </div>
              </div>
              <button
                onClick={() => disconnect(session.session_id)}
                className="px-3 py-2 bg-red-100 text-red-700 text-xs font-bold rounded-lg hover:bg-red-200 transition-all"
                style={{ minHeight: '36px' }}
              >
                Disconnect
              </button>
            </div>
          );
        })}

        {sessions.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500 text-sm">
            <div className="text-4xl mb-2">📱</div>
            No WhatsApp accounts linked yet
          </div>
        )}
      </div>

      {/* Safety Warning */}
      <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <div className="flex items-start gap-2">
          <span className="text-lg flex-shrink-0">⚠️</span>
          <div>
            <p className="text-xs font-bold text-yellow-900 mb-1">Important Safety Notice</p>
            <p className="text-xs text-yellow-800 leading-relaxed">
              Using personal WhatsApp for bulk messaging may violate WhatsApp's Terms of Service. 
              Send responsibly to avoid account restrictions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSessionManager;
